import React, { useState, useEffect } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api.ts";
// price conversion utilities
import { useCurrencyUtils } from "../../utils/currencyUtils";
import { useCurrency } from "../../context/CurrencyContext";
import { X, Upload, FileText } from "lucide-react";
import placeholder from "../../assets/Car-place-holder-PNG.png";
import PaymentPopup from "../common/PaymentPopup";
import Pagination from "../common/Pagination";
import jsPDF from "jspdf";
import { Banknote, Search, Eye } from "lucide-react";
import html2pdf from "html2pdf.js";
import dayjs from "dayjs";
import { applyStandardPdfStyles } from "../../utils/pdfUtils";
import skyautologo from "../../assets/skyAutologo.png";

const WonVehicles = () => {
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const { user } = useAuth();
  const navigate = useNavigate();
  const { displayPriceWithSymbol, convertPrice } = useCurrencyUtils();
  const { currency, currencies } = useCurrency();
  const [wonVehicles, setWonVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [auctionType, setAuctionType] = useState("auction"); // Default to auction
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchLotNumber, setSearchLotNumber] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    paid: 0,
    pending: 0,
    "partial-payment": 0,
  });
  const [childBuyers, setChildBuyers] = useState([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState(null);

  const selectedChildBuyer = React.useMemo(() => {
    if (!selectedBuyerId) return null;
    return (
      childBuyers.find((child) => child.buyerId === selectedBuyerId) || null
    );
  }, [childBuyers, selectedBuyerId]);

  const selectedBuyerDisplayName =
    selectedChildBuyer?.user?.name || user?.name || "";
  const selectedBuyerDisplayClientId =
    selectedChildBuyer?.user?.clientId || user?.clientId || "";

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchLotNumber);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchLotNumber]);

  // Fetch child buyers list (corporate buyer dropdown)
  useEffect(() => {
    const fetchChildBuyers = async () => {
      try {
        const response = await api.get("/users/corporatebuyers", {
          params: { limit: 100 },
        });
        const data = response.data?.data || [];
        setChildBuyers(data);
      } catch (error) {
        // If the user is not a corporate buyer or endpoint fails, silently ignore
        console.debug("Unable to load corporate child buyers:", error?.message);
        setChildBuyers([]);
      }
    };

    fetchChildBuyers();
  }, []);

  // Fetch counts for each tab from server
  useEffect(() => {
    const fetchTabCounts = async () => {
      try {
        // Base params for all queries - using flat filter structure like the API expects
        const baseParams = {
          page: 1,
          limit: 1, // Just need the total count, not the data
          orderBy: "id",
          order: "desc",
          "filters[isSold]": "1",
          "filters[isFromBuyNow]": auctionType === "buynow" ? "1" : "0",
        };

        if (selectedBuyerId) {
          baseParams.buyerId = selectedBuyerId;
        }

        // Fetch all counts in parallel
        const [allResponse, pendingResponse, partialResponse, paidResponse] =
          await Promise.all([
            // All - no status filter
            api.get(`/users/mywonvehicles`, {
              params: baseParams,
            }),
            // Pending - invoicePaymentStatus = 1
            api.get(`/users/mywonvehicles`, {
              params: {
                ...baseParams,
                "filters[invoicePaymentStatus]": 1,
              },
            }),
            // Partial Payment - invoicePaymentStatus = 2
            api.get(`/users/mywonvehicles`, {
              params: {
                ...baseParams,
                "filters[invoicePaymentStatus]": 2,
              },
            }),
            // Paid - invoicePaymentStatus = 3
            api.get(`/users/mywonvehicles`, {
              params: {
                ...baseParams,
                "filters[invoicePaymentStatus]": 3,
              },
            }),
          ]);

        // Extract counts from responses
        setTabCounts({
          all: allResponse.data?.total || 0,
          pending: pendingResponse.data?.total || 0,
          "partial-payment": partialResponse.data?.total || 0,
          paid: paidResponse.data?.total || 0,
        });
      } catch (error) {
        console.error("Error fetching tab counts:", error);
        // Set counts to 0 on error
        setTabCounts({
          all: 0,
          paid: 0,
          pending: 0,
          "partial-payment": 0,
        });
      }
    };

    fetchTabCounts();
  }, [auctionType, selectedBuyerId]);

  // Fetch won vehicles from API with server-side pagination
  useEffect(() => {
    const fetchWonVehicles = async () => {
      try {
        setLoading(true);

        // Build query parameters for server-side pagination and filtering
        const params = {
          page: currentPage,
          limit: pageSize,
          orderBy: "id",
          order: "desc",
          "filters[isSold]": "1",
          "filters[isFromBuyNow]": auctionType === "buynow" ? "1" : "0",
        };

        if (selectedBuyerId) {
          params.buyerId = selectedBuyerId;
        }

        // Add status filter if not "all"
        if (filterStatus !== "all") {
          // Map status strings to their corresponding IDs from the database
          const statusIdMap = {
            pending: 1,
            "partial-payment": 2,
            paid: 3,
          };

          if (statusIdMap[filterStatus]) {
            params["filters[invoicePaymentStatus]"] = statusIdMap[filterStatus];
          }
        }

        // Add search parameter if provided
        if (debouncedSearch && debouncedSearch.trim() !== "") {
          params.search = debouncedSearch.trim();
        }

        const response = await api.get(`/users/mywonvehicles`, { params });

        // Extract the actual data array from the paginated response
        const vehiclesData = response.data.data || response.data;

        // Check if vehiclesData is an array
        if (!Array.isArray(vehiclesData)) {
          console.error("API response data is not an array:", vehiclesData);
          setWonVehicles([]);
          setLoading(false);
          return;
        }

        setWonVehicles(vehiclesData);

        // Update pagination state from API response
        if (response.data.page && response.data.pages && response.data.total) {
          setPagination({
            currentPage: response.data.page,
            totalPages: response.data.pages,
            totalItems: response.data.total,
            itemsPerPage: pageSize,
          });
        } else {
          console.log("No pagination data in API response");
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching won vehicles:", error);
        setLoading(false);
        // Set empty array on error
        setWonVehicles([]);
      }
    };

    fetchWonVehicles();
  }, [
    currentPage,
    filterStatus,
    debouncedSearch,
    pageSize,
    auctionType,
    selectedBuyerId,
  ]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUnderApprovalAmount = (vehicle) => {
    const payments =
      vehicle?.buyerInvoiceDetails?.[0]?.buyerInvoice?.buyerInvoicePayments ||
      vehicle?.buyerInvoicePayments ||
      [];

    return (payments || []).reduce((sum, payment) => {
      const amount = Number(payment?.amountPaid || 0);
      if (payment?.isApproved === null && amount > 0) {
        return sum + amount;
      }
      return sum;
    }, 0);
  };

  const getStatusColor = (vehicle) => {
    const status = getVehicleStatus(vehicle);
    
    if (status === "under-approval") {
      return "text-white bg-orange-500";
    }

    const statusName =
      vehicle.buyerInvoiceDetails[0]?.buyerInvoice?.invoicePaymentStatus?.name;

    if (statusName === "Paid") {
      return "text-white bg-green-700";
    } else if (statusName === "Pending") {
      return "text-white bg-yellow-700";
    }
    return "text-white bg-gray-700";
  };

  const getStatusText = (vehicle) => {
    const status = getVehicleStatus(vehicle);
    
    if (status === "under-approval") {
      return "Under Approval";
    }

    const statusName =
      vehicle.buyerInvoiceDetails[0]?.buyerInvoice?.invoicePaymentStatus?.name;

    if (statusName === "Paid") {
      return translate("wonVehicles.tabs.paid");
    } else if (statusName === "Pending") {
      return translate("wonVehicles.tabs.pendingPayment");
    } else if (statusName === "Partial Payment") {
      return translate("wonVehicles.tabs.partialPayment");
    }
    return translate("wonVehicles.tabs.allVehicles");
  };

  const getVehicleStatus = (vehicle) => {
    const underApprovalAmount = getUnderApprovalAmount(vehicle);
    if (underApprovalAmount > 0) {
      return "under-approval";
    }

    return (
      vehicle.buyerInvoiceDetails[0]?.buyerInvoice?.invoicePaymentStatus
        ?.slug || "unknown"
    );
  };

  // Payment popup functions
  const handlePayNow = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowPaymentPopup(true);
  };

  const handleClosePopup = () => {
    setShowPaymentPopup(false);
    setSelectedVehicle(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the vehicles list after successful payment
    window.location.reload();
  };

  // PDF Download function
  const handleDownloadPDF = (vehicle) => {
    if (!vehicle) return;

    const createdAt = vehicle?.soldDate
      ? new Date(vehicle.soldDate).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      : "N/A";

    const buyer =
      vehicle?.buyerInvoiceDetails?.[0]?.buyerInvoice?.buyer?.user || {};
    const invoice = vehicle?.buyerInvoiceDetails?.[0]?.buyerInvoice || {};

    const soldPrice = vehicle?.soldPrice || 0;
    const totalAmount = invoice?.totalAmount || 0;
    const remainingAmount = invoice?.remainingAmount || totalAmount;
    const paymentStatus = getStatusText(vehicle);

    const currencyTextSymbols = {
      USD: "$",
      AED: "AED",
      IQD: "IQD",
    };
    const currentSymbol = currencyTextSymbols[currency] || currency;
    const invoiceNumber = invoice?.id || "N/A";

    // HTML template
    const htmlContent = `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="UTF-8">
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; margin: 25px; background: #fff; color: #2d3436; }
      .container { border: 1px solid #e0e0e0; border-radius: 10px; padding: 20px; }
      .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #135EA0; padding-bottom: 12px; margin-bottom: 20px; }
      .header img { height: 55px; }
      .title { font-size: 22px; font-weight: 700; color: #135EA0; letter-spacing: 1px; }
      .section { margin-bottom: 18px; border: 1px solid #eee; border-radius: 8px; padding: 12px 15px; }
      .section h3 { color: #135EA0; margin-bottom: 8px; font-size: 14px; }
      .section table { width: 100%; border-collapse: collapse; margin-top: 5px; }
      .section table td { padding: 6px 4px; font-size: 13px; border-bottom: 1px solid #eee; }
      .section table td:first-child { font-weight: 600; color: #555; width: 40%; }
      .footer { text-align: center; font-size: 12px; margin-top: 15px; border-top: 1px solid #ddd; padding-top: 8px; }
      .note { margin-top: 15px; font-size: 12px; font-style: italic; text-align: center; }
    </style>
  </head>
  <body>
    <div class="container">
      <div class="header">
        <img src="${skyautologo}" />
        <div class="title">INVOICE</div>
      </div>

      <div class="section">
        <h3>Buyer Information</h3>
        <table>
          <tr><td>Name</td><td>${buyer.name || "N/A"} (${buyer.clientId || ""})</td></tr>
          <tr><td>Email</td><td>${buyer.email || "N/A"}</td></tr>
          <tr><td>Phone</td><td>${buyer.phone || buyer.mobileNo || "N/A"}</td></tr>
        </table>
      </div>

      <div class="section">
        <h3>Vehicle Information</h3>
        <table>
          <tr><td>Vehicle Name</td><td>${vehicle.name || "N/A"}</td></tr>
          <tr><td>Lot Number</td><td>${vehicle.lotNumber || "N/A"}</td></tr>
          <tr><td>VIN</td><td>${vehicle.vinNumber || vehicle.vin || "N/A"}</td></tr>
          <tr><td>Sold Date</td><td>${createdAt}</td></tr>
        </table>
      </div>

      <div class="section">
        <h3>Invoice Details</h3>
        <table>
          <tr><td>Winning Bid</td><td>${currentSymbol} ${soldPrice.toLocaleString()}</td></tr>
          <tr><td>Invoice Amount (incl. VAT)</td><td>${currentSymbol} ${totalAmount.toLocaleString()}</td></tr>
          <tr><td>Remaining Amount</td><td>${currentSymbol} ${remainingAmount.toLocaleString()}</td></tr>
          <tr><td>Payment Status</td><td>${paymentStatus}</td></tr>
          <tr><td>Invoice No</td><td>${invoiceNumber}</td></tr>
        </table>
      </div>

      <div class="footer">
        <p><strong>Sky Auto Auction</strong></p>
        <p>+971 50 222 0000 | skyautotrade.aws@gmail.com</p>
        <p>www.skyautoauction.com</p>
      </div>

      <div class="note">
        NOTE: This document is system-generated and serves as an official invoice record.
      </div>
    </div>
  </body>
  </html>
  `;

    const element = document.createElement("div");
    element.innerHTML = htmlContent;

    const options = {
      margin: [10, 10, 10, 10],
      filename: `Invoice-${vehicle.name?.replace(/[^a-zA-Z0-9]/g, "_") || "vehicle"}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    const generatedDate = dayjs().format("MMMM D, YYYY [at] hh:mm A");
    html2pdf().set(options).from(element).toPdf().get("pdf").then((pdf) => {
      applyStandardPdfStyles(pdf, generatedDate);
    }).save();
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, debouncedSearch]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Returns buyer name according to parent/child visibility rules
  const getBuyerName = (buyerInvoiceDetail) => {
    const detail = buyerInvoiceDetail;
    const buyer = detail?.buyer;

    // Use backend-provided displayName if available
    if (detail?.displayName) return detail.displayName;

    if (!buyer) return "-";

    // Case 1: Logged-in user is parent → show child name
    if (buyer.parentId && buyer.user?.name && buyer.parentId === user.id) {
      return buyer.user.name;
    }

    // Case 2: Logged-in user is child or another → show parent name if exists
    if (buyer.parentBuyer?.user?.name) {
      return buyer.parentBuyer.user.name;
    }

    // Case 3: Fallback → show buyer name
    return buyer.user?.name || "-";
  };

  // Returns buyer clientId according to parent/child visibility rules
  const getBuyerClientId = (buyerInvoiceDetail) => {
    const detail = buyerInvoiceDetail;

    // Use backend-provided displayClientId if available
    if (detail?.displayClientId) return detail.displayClientId;

    const buyer = detail?.buyer;
    if (!buyer) return "-";

    if (buyer.parentId && buyer.user?.clientId && buyer.parentId === user.id) {
      return buyer.user.clientId;
    }

    if (buyer.parentBuyer?.user?.clientId) {
      return buyer.parentBuyer.user.clientId;
    }

    return buyer.user?.clientId || "-";
  };

  // Returns email according to parent/child visibility rules
  const getBuyerEmail = (buyerInvoiceDetail) => {
    const buyer = buyerInvoiceDetail?.buyer;
    if (!buyer) return "-";

    // Case 1: Logged-in user is parent → show child email
    if (buyer.parentId && buyer.user?.email && buyer.parentId === user.id) {
      return buyer.user.email;
    }

    // Case 2: Logged-in user is child or another → show parent email if exists
    if (buyer.parentBuyer?.user?.email) {
      return buyer.parentBuyer.user.email;
    }

    // Case 3: Fallback → show buyer email (child email)
    if (buyer.user?.email) {
      return buyer.user.email;
    }

    return "-";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div
        className="bg-card rounded-lg shadow-md"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Header */}
        {/* <div className=' font-medium rounded-t-[16px]'>
    <div className='p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 text-white bg-primary border-b border-black pb-2'>
      <h2 className='text-white sm:text-lg font-semibold'>
        {translate("wonVehicles.heading")}
      </h2>
    </div>
  </div> */}

        {/* header with auction buynow */}
        {/* <div className='bg-card font-medium rounded-t-[16px] border-b border-default'>
  <div className='flex flex-wrap items-center justify-between px-5 py-3 rounded-t-[12px] font-semibold bg-primary border-b border-black'>
    
   
    <h2 className='text-white text-base sm:text-lg font-semibold whitespace-nowrap'>
      {translate("wonVehicles.heading")}
    </h2>

  
    <div className='flex items-center bg-black/10 p-1 rounded-lg border border-white/10 ms-auto scale-90 sm:scale-100 origin-right'>
      <nav className={`flex ${isRTL ? "space-x-reverse" : "space-x-1"}`}>
        {[
          { key: "auction", label: translate("wonVehicles.auctionTabs.auction") },
          { key: "buynow", label: translate("wonVehicles.auctionTabs.buyNow") },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setAuctionType(tab.key)}
            className={`px-4 py-1.5 rounded-md font-medium text-xs transition-all duration-200 ${
              auctionType === tab.key
                ? "bg-white text-primary shadow-sm"
                : "text-white/80 hover:bg-white/10 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  </div>
</div> */}

        {/* new header with filters and search  with auction and buynow*/}
        <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
          {/* Main Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]">
            {/* Left Side: Heading Only */}
            <div className="flex flex-col justify-center">
              <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
                {translate("wonVehicles.heading")}
              </h2>
              {/* {selectedBuyerDisplayName && (
                <p className="text-xs text-white/80 mt-1">
                  {translate("wonVehicles.labels.currentBuyer") || "Showing for"} {selectedBuyerDisplayName}
                  {selectedBuyerDisplayClientId ? ` (${selectedBuyerDisplayClientId})` : ""}
                </p>
              )} */}
            </div>

            {/* Right Group: Search Bar (Left) and Filters (Right) */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 ms-auto lg:ms-0 w-full lg:w-auto">
              {/* Search Input Integrated on the Right side */}
              <div className="relative w-full md:w-60 xl:w-72">
                <input
                  type="text"
                  placeholder={translate(
                    "wonVehicles.filters.searchByLotNumber",
                  )}
                  className={`w-full px-3 py-1.5 ${isRTL ? "pr-9" : "pl-9"} bg-black/10 border border-white/10 rounded-full text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/30 text-xs transition-all`}
                  value={searchLotNumber || ""}
                  onChange={(e) => setSearchLotNumber(e.target.value)}
                />
                <svg
                  className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-white/70`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {/* Status Filters */}
              <div className="flex items-center overflow-x-auto no-scrollbar">
                <nav
                  className={`flex gap-1.5 sm:gap-2 ${isRTL ? "flex-row-reverse" : ""}`}
                >
                  {[
                    {
                      key: "all",
                      label: translate("wonVehicles.tabs.allVehicles"),
                      count: tabCounts.all,
                    },
                    {
                      key: "paid",
                      label: translate("wonVehicles.tabs.paid"),
                      count: tabCounts.paid,
                    },
                    {
                      key: "pending",
                      label: translate("wonVehicles.tabs.pendingPayment"),
                      count: tabCounts.pending,
                    },
                    {
                      key: "partial-payment",
                      label: translate("wonVehicles.tabs.partialPayment"),
                      count: tabCounts["partial-payment"],
                    },
                  ].map((filter) => (
                    <button
                      key={filter.key}
                      onClick={() => setFilterStatus(filter.key)}
                      className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap ${filterStatus === filter.key
                          ? "bg-white text-primary shadow-md"
                          : "bg-black/10 text-white/80 hover:bg-white/10 hover:text-white border border-white/5"
                        }`}
                    >
                      <span>{filter.label}</span>
                      <span
                        className={`text-[9px] opacity-70 ${filterStatus === filter.key ? "text-primary" : "text-white"}`}
                      >
                        ({filter.count})
                      </span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Secondary Header (Separate Container) */}
          <div className="px-4 sm:px-5 py-2 flex justify-end items-center bg-card border-b border-default">
            <div className="flex items-center gap-4">
              {childBuyers.length > 0 && (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedBuyerId || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedBuyerId(val ? Number(val) : null);
                      setCurrentPage(1);
                    }}
                    className="bg-page p-2 rounded-full text-white text-xs focus:outline-none"
                  >
                    {/* Default "All" option selected initially */}
                    <option value="">Select Buyer</option>
                    {childBuyers.map((child) => (
                      <option key={child.buyerId} value={child.buyerId}>
                        {child.user?.name || child.businessName || "-"}
                        {child.user?.clientId
                          ? ` (${child.user.clientId})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <nav
                className={`flex bg-secondary py-1 px-2 rounded-full border border-default scale-90 sm:scale-95 origin-right ${isRTL ? "space-x-reverse" : "space-x-1"}`}
              >
                {[
                  {
                    key: "auction",
                    label: translate("wonVehicles.auctionTabs.auction"),
                  },
                  {
                    key: "buynow",
                    label: translate("wonVehicles.auctionTabs.buyNow"),
                  },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setAuctionType(tab.key)}
                    className={`px-4 py-1 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 ${auctionType === tab.key
                        ? "bg-primary text-white shadow-sm"
                        : "bg-transparent text-black/70 hover:text-primary"
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Auction Type Tabs previos ui */}
        {/* <div className=' '>
          <div className='px-5 py-2'>
            <div className=' border border-default w-fit p-2 rounded-lg'>
              <nav
                className={`flex ${isRTL ? "space-x-reverse" : "space-x-1"}`}
              >
                {[
                  {
                    key: "auction",
                    label: translate("wonVehicles.auctionTabs.auction"),
                  },
                  {
                    key: "buynow",
                    label: translate("wonVehicles.auctionTabs.buyNow"),
                  },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setAuctionType(tab.key)}
                    className={`px-6 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      auctionType === tab.key
                        ? "bg-gradient-to-r from-primary to-secondary text-white shadow-lg"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div> */}

        <div className="space-y-1">
          {/* Filter Navigation  previous ui */}
          {/* <div className='border-b border-default'>
            <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 py-4 px-5'>
              <nav
                className={`flex ${
                  isRTL ? "space-x-reverse" : "space-x-2"
                } overflow-x-auto pb-2 lg:pb-0`}
              >
                {[
                  {
                    key: "all",
                    label: translate("wonVehicles.tabs.allVehicles"),
                    count: tabCounts.all,
                  },
                  {
                    key: "paid",
                    label: translate("wonVehicles.tabs.paid"),
                    count: tabCounts.paid,
                  },
                  {
                    key: "pending",
                    label: translate("wonVehicles.tabs.pendingPayment"),
                    count: tabCounts.pending,
                  },
                  {
                    key: "partial-payment",
                    label: translate("wonVehicles.tabs.partialPayment"),
                    count: tabCounts["partial-payment"],
                  },
                ].map((filter) => {
                  return (
                    <button
                      key={filter.key}
                      onClick={() => setFilterStatus(filter.key)}
                      className={`flex-shrink-0 px-3 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center gap-2
            ${
              filterStatus === filter.key
                ? "bg-gradient-to-r from-primary to-secondary text-white shadow-md"
                : "bg-secondary text-black/70 hover:bg-primary hover:text-white"
            }`}
                    >
                      <span>{filter.label}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                          filterStatus === filter.key
                            ? "bg-white/20 text-white"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {filter.count}
                      </span>
                    </button>
                  );
                })}
              </nav>

             
              <div className='flex items-center'>
                <div className='relative'>
                  <input
                    type='text'
                    placeholder={translate(
                      "wonVehicles.filters.searchByLotNumber"
                    )}
                    className={`w-full sm:w-80 px-4 py-2 ${
                      isRTL ? "pr-10" : "pl-10"
                    } bg-page text-description border border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-primarytext-sm`}
                    value={searchLotNumber || ""}
                    onChange={(e) => setSearchLotNumber(e.target.value)}
                  />
                  <svg
                    className={`absolute ${
                      isRTL ? "right-3" : "left-3"
                    } top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                    />
                  </svg>
                </div>
                {searchLotNumber && (
                  <button
                    onClick={() => setSearchLotNumber("")}
                    className='ml-2 p-2 text-gray-400 hover:text-gray-600 transition-colors'
                    title='Clear search'
                  >
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M6 18L18 6M6 6l12 12'
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div> */}

          {/* Vehicles List */}
          <div className="space-y-3">
            {wonVehicles.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4 flex items-center justify-center">
                  <div className="border h-12 w-12 rounded-full">
                    <svg
                      className="mx-auto h-12 w-12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-white sm:text-lg font-medium mb-2">
                  {translate("wonVehicles.messages.noWonVehiclesFound")}
                </h3>
                <p className="text-description">
                  {translate("wonVehicles.messages.noWonVehiclesYet")}
                </p>
              </div>
            ) : (
              wonVehicles.map((vehicle) => (
                // const buyerDetail = vehicle?.buyerInvoiceDetails?.[0];
                // const buyerId = buyerDetail?.buyer?.id;
                // const isChildRecord =
                //   buyerDetail?.buyer?.parentId &&
                //   user?.buyer?.id &&
                //   buyerDetail.buyer.parentId === user.buyer.id;

                // return (
                //   <div
                //     key={vehicle.id}
                //     className={`border border-gray-800/50 rounded-xl shadow-sm p-4 hover:shadow-lg hover:shadow-black/30 transition-all duration-200 ${
                //       isChildRecord ? "bg-gray-900/40" : "bg-page"
                //     }`}
                //   >
                <div
                  key={vehicle.id}
                  className={`border border-gray-800/50 rounded-xl shadow-sm p-4 hover:shadow-lg hover:shadow-black/30 transition-all duration-200 ${!!vehicle?.buyerInvoiceDetails?.[0]?.buyerInvoice?.buyer
                      ?.parentId
                      ? "bg-gray-900/40"
                      : "bg-page"
                    }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
                    {/* Vehicle Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={
                          (vehicle?.images &&
                            vehicle?.images?.length > 0 &&
                            vehicle?.images[0]?.url) ||
                          vehicle?.images[0]?.imageUrl ||
                          placeholder
                        }
                        alt={vehicle?.name}
                        onError={(e) => (e.target.src = placeholder)}
                        className="w-full sm:w-28 h-40 sm:h-20 object-cover rounded-lg border border-gray-800/40"
                      />
                    </div>

                    {/* Vehicle Details */}
                    <div className="flex-1 min-w-0 space-y-4">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:gap-6">
                        <div className="flex-1 space-y-3">
                          <h3 className="text-lg font-semibold text-gray-100">
                            {vehicle?.name}
                          </h3>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                            <div>
                              <span className="text-gray-400">
                                {translate("wonVehicles.labels.lotNumber")}:
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-gray-200`}
                              >
                                {vehicle?.lotNumber || "—"}
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-400">
                                {translate("wonVehicles.labels.invoiceNumber")}:
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-gray-200`}
                              >
                                {vehicle?.buyerInvoiceDetails?.[0]?.buyerInvoice
                                  ?.invoiceNumber ||
                                  vehicle?.buyerInvoiceDetails?.[0]
                                    ?.buyerInvoice?.id ||
                                  "N/A"}
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-400">
                                {translate("wonVehicles.labels.buyer") ||
                                  "Buyer"}
                                :
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-gray-200`}
                              >
                                {/* {getBuyerName(
                                  vehicle?.buyerInvoiceDetails?.[0],
                                )}{" "}
                                (
                                {getBuyerClientId(
                                  vehicle?.buyerInvoiceDetails?.[0],
                                )}
                                ) */}
                                {
                                  vehicle?.buyerInvoiceDetails?.[0]
                                    ?.buyerInvoice?.buyer?.user?.name
                                }{" "}
                                (
                                {
                                  vehicle?.buyerInvoiceDetails?.[0]
                                    ?.buyerInvoice?.buyer?.user?.clientId
                                }
                                )
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-400">
                                {translate("wonVehicles.labels.winningBid")}:
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-blue-400`}
                              >
                                {displayPriceWithSymbol(
                                  convertPrice(vehicle?.soldPrice || 0),
                                )}
                              </span>
                            </div>

                            {/* <div>
  <span className="text-gray-400">
    Child Email:
  </span>
  <span className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-blue-400`}>
    {getBuyerEmail(vehicle?.buyerInvoiceDetails?.[0])}
  </span>
</div> */}

                            <div>
                              <span className="text-gray-400">
                                {translate("wonVehicles.labels.invoiceAmount")}:
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-gray-200`}
                              >
                                {displayPriceWithSymbol(
                                  convertPrice(
                                    vehicle?.buyerInvoiceDetails?.[0]
                                      ?.buyerInvoice?.totalAmount || 0,
                                  ),
                                )}{" "}
                                <span className="text-gray-500 text-xs">
                                  ({translate("wonVehicles.labels.vatIncluded")}
                                  )
                                </span>
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-400">
                                {translate(
                                  "wonVehicles.labels.remainingAmount",
                                )}
                                :
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-red-400`}
                              >
                                {displayPriceWithSymbol(
                                  convertPrice(
                                    vehicle?.buyerInvoiceDetails?.[0]
                                      ?.buyerInvoice?.remainingAmount || 0,
                                  ),
                                )}
                              </span>

                              {getUnderApprovalAmount(vehicle) > 0 && (
                                <div className="mt-1">
                                  <span className="text-gray-400">
                                    Under Approval:
                                  </span>
                                  <span
                                    className={`${isRTL ? "mr-2" : "ml-2"
                                      } text-xs font-medium text-orange-400`}
                                  >
                                    {displayPriceWithSymbol(
                                      convertPrice(
                                        getUnderApprovalAmount(vehicle),
                                      ),
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div>
                              <span className="text-gray-400">
                                {translate("wonVehicles.labels.soldDate")}:
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"} font-medium text-gray-200`}
                              >
                                {formatDate(vehicle?.soldDate)}
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-400">
                                {translate("wonVehicles.labels.status")}:
                              </span>
                              <span
                                className={`${isRTL ? "mr-2" : "ml-2"
                                  } px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(vehicle)}`}
                              >
                                {getStatusText(vehicle)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {/* <div className="flex-shrink-0 lg:w-44 mt-4 lg:mt-0">
                          <div className="flex flex-col gap-2">
                            {(getVehicleStatus(vehicle) === "partial-payment" ||
                              getVehicleStatus(vehicle) === "pending") && (
                                <button
                                  onClick={() => handlePayNow(vehicle)}
                                  className="w-full bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-lg transition-colors text-sm font-medium shadow-sm"
                                >
                                  {translate("wonVehicles.labels.payNow")}
                                </button>
                              )}

                            <button
                              onClick={() => navigate(`/${vehicle.slug}`)}
                              className="w-full bg-bg-medium border border-gray-700 hover:bg-gray-800 text-gray-100 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium shadow-sm"
                            >
                              {translate("wonVehicles.buttons.viewDetails")}
                            </button>

                            <button
                              onClick={() => handleDownloadPDF(vehicle)}
                              className="w-full border border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-gray-100 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              {translate("wonVehicles.labels.downloadDocuments")}
                            </button>
                          </div>
                        </div> */}
                        <div className="flex-shrink-0 mt-4 lg:mt-0">
                          {/* Row Container: Buttons stay in a line */}
                          <div className="flex flex-row items-center justify-end gap-2 min-h-[44px]">
                            {/* 1. PAY NOW (Icon: 💰 or Credit Card) */}
                            {(getVehicleStatus(vehicle) === "partial-payment" ||
                              getVehicleStatus(vehicle) === "pending") && 
                               getVehicleStatus(vehicle) !== "under-approval" &&
                              (
                                <div className="relative h-10 w-10">
                                  <button
                                    onClick={() => handlePayNow(vehicle)}
                                    className="absolute right-0 top-0 h-10 w-10 hover:w-32 flex items-center bg-primary hover:bg-primary-dark text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                                  >
                                    <div className="flex items-center pl-3 pr-4">
                                      <Banknote
                                        size={20}
                                        className="flex-shrink-0 "
                                      />
                                      <span className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                        {translate("wonVehicles.labels.payNow")}
                                      </span>
                                    </div>
                                  </button>
                                </div>
                              )}

                            {/* 2. VIEW DETAILS (Icon: Eye) */}
                            <div className="relative h-10 w-10">
                              <button
                                onClick={() => navigate(`/${vehicle.slug}`)}
                                className="absolute right-0 top-0 h-10 w-10 hover:w-44 flex items-center bg-gray-800 border border-gray-700 hover:bg-gray-700 text-gray-100 rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                              >
                                {/* 1. Wrapper with fixed layout */}
                                <div className="flex items-center h-full w-full">
                                  {/* 2. Icon Container: Always centered in the first 40px */}
                                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                    <Eye size={20} />
                                  </div>

                                  {/* 3. Text: Slides out to the right (visually left because of absolute right-0) */}
                                  <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                    {translate(
                                      "wonVehicles.buttons.viewDetails",
                                    )}
                                  </span>
                                </div>
                              </button>
                            </div>

                            {/* 3. DOWNLOAD PDF */}
                            <div className="relative h-10 w-10">
                              <button
                                onClick={() => handleDownloadPDF(vehicle)}
                                className="absolute right-0 top-0 h-10 w-10 hover:w-40 flex items-center bg-gray-800 border border-gray-700 hover:bg-gray-700 text-gray-100 rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                              >
                                {/* Full-width flex container to manage icon and text positioning */}
                                <div className="flex items-center h-full w-full">
                                  {/* Icon Wrapper: Exactly 40px wide to match the initial button state */}
                                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="w-5 h-5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                      />
                                    </svg>
                                  </div>

                                  {/* Text: Slides out from the center-anchor. pr-4 provides the end padding. */}
                                  <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                    {translate(
                                      "wonVehicles.labels.downloadDocuments",
                                    )}
                                  </span>
                                </div>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Messages */}
                      {getVehicleStatus(vehicle) === "partial-payment" && (
                        <div
                          className={`mt-4 flex items-center gap-2 text-blue-400 ${isRTL ? "flex-row-reverse" : ""}`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {translate(
                              "wonVehicles.messages.partialPaymentReceived",
                            )}
                          </span>
                        </div>
                      )}

                      {getVehicleStatus(vehicle) === "pending" && (
                        <div
                          className={`mt-4 flex items-center gap-2 text-red-500 ${isRTL ? "flex-row-reverse" : ""}`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {translate("wonVehicles.labels.paymentDue")}
                          </span>
                        </div>
                      )}

                      {getVehicleStatus(vehicle) === "paid" && (
                        <div
                          className={`mt-4 flex items-center gap-2 text-emerald-500 ${isRTL ? "flex-row-reverse" : ""}`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {translate("wonVehicles.messages.paymentCompleted")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 ">
              <Pagination
                totalPages={pagination.totalPages}
                currentPage={pagination.currentPage}
                setCurrentPage={handlePageChange}
              />
            </div>
          )}

          {/* Payment Receipt Upload Popup */}
          {/* Payment Popup */}
        </div>
      </div>
      <PaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePopup}
        selectedItem={selectedVehicle}
        itemType="vehicle"
        onPaymentSuccess={handlePaymentSuccess}
      />
    </>
  );
};

export default WonVehicles;
